/**
 * King Living Component Wrapper Override
 *
 * This overrides the pwa-contentful Component wrapper to use king-living models.
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {componentPropType} from '@salesforce/commerce-sdk-react/components';
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {CONTENTFUL_METADATA} from '@amblique/king-living/app/contentful/metadata';
// Import from king-living models to get overridden components
import * as ContentfulComponents from '@amblique/king-living/app/contentful/models';
// Import king-living hook overrides
import {useTeaserTileById} from '@amblique/king-living/app/contentful/hooks/use-teaser-tile';
import {useTeaserTileBlockById} from '@amblique/king-living/app/contentful/hooks/use-teaser-tile-block';
import {useSplitPanelDoubleImageBlockById} from '@amblique/king-living/app/contentful/hooks/use-split-panel-double-image-block';
import {useMediaContentSectionById} from '@amblique/king-living/app/contentful/hooks/use-media-content-section';
import {useMediaWithScrollableTextById} from '@amblique/king-living/app/contentful/hooks/use-media-with-scrollable-text';
import {useMediaCarouselById} from '@amblique/king-living/app/contentful/hooks/use-media-carousel';
import {useKingDifferenceMediaCf013ById} from '@amblique/king-living/app/contentful/hooks/use-king-difference-media-cf013';
import {inflector} from '@amblique/king-living/shared/helpers/inflector';
import {
  resolveJumpAnchorId,
  resolveJumpLinkMarginTop,
} from '@amblique/king-living/app/contentful/utils/resolve-jump-anchor-id';

// King Living hook overrides (use 'full' variant by default to include image data)
const KING_LIVING_HOOKS = {
  TeaserTile: useTeaserTileById,
  TeaserTileBlock: useTeaserTileBlockById,
  SplitPanelDoubleImageBlock: useSplitPanelDoubleImageBlockById,
  MediaContentSection: useMediaContentSectionById,
  MediaWithScrollableText: useMediaWithScrollableTextById,
  MediaCarousel: useMediaCarouselById,
  KingDifferenceMediaCf013: useKingDifferenceMediaCf013ById,
};

/**
 * Generic component wrapper for Contentful entries
 * - Resolves component by __typename
 * - Auto-fetches full data if incomplete (Three-Tier Fragment Strategy)
 * - Applies styling and renders appropriate component
 *
 * @param {object} props
 * @param {object} props.component - Contentful component data
 * @returns {React.ReactElement}
 */
export const Component = ({component, as, ...props}) => {
  if (!component?.sys?.id) {
    return null;
  }

  const {className: propsClassName, ...propsRest} = props;

  const {__typename, sys: {id}} = component;

  const baseComponentClassName = `component--${inflector.tableize(__typename, '-')} component--${id}`;

  const ResolvedComponent = as || ContentfulComponents[__typename];
  const metadata = CONTENTFUL_METADATA[__typename];

  // Get Chakra UI styling for this component type
  const componentStyle = useStyleConfig('shared/component', {component});

  // Check if component data is complete (has all expected fields)
  const isComplete = useMemo(() => {
    if (!metadata?.fields) return true;

    return metadata.fields.every((fieldName) => Object.hasOwn(component, fieldName));
  }, [component, metadata]);

  // Auto-fetch full data if incomplete
  // Use king-living hook override if available (these default to 'full' variant)
  const useEntryById = KING_LIVING_HOOKS[__typename] || metadata?.hook;
  const shouldFetch = !isComplete && useEntryById && Boolean(component.sys?.id);

  const {data: fullComponent, isFetching} = useEntryById?.(
    id,
    {
      queryOptions: {
        enabled: shouldFetch,
      },
    }
  ) || {data: null, isFetching: false};

  // Use enriched data if available, otherwise use provided data
  const finalComponent = fullComponent || component;

  // Recalculate completeness after fetching
  const isFinalComplete = useMemo(() => {
    if (!metadata?.fields) return true;

    return metadata.fields.every((fieldName) => Object.hasOwn(finalComponent, fieldName));
  }, [finalComponent, metadata]);

  // KL-1058: apply the configured anchor id and sticky-header offset.
  const jumpAnchorId = resolveJumpAnchorId(finalComponent) || id;
  const jumpLinkMarginTop = resolveJumpLinkMarginTop(finalComponent);

  const mergedClassName = [propsClassName, baseComponentClassName].filter(Boolean).join(' ');

  const componentProps = {
    ...componentStyle,
    ...propsRest,

    className: mergedClassName,
    id: propsRest.id ?? jumpAnchorId,
    style: {
      ...componentStyle.style,
      ...propsRest.style,
      ...(jumpLinkMarginTop ? {scrollMarginTop: jumpLinkMarginTop} : {}),
    },
    isComplete: isFinalComplete,
    isFetching,
  };

  return ResolvedComponent ? (
    <ResolvedComponent component={finalComponent} {...componentProps} />
  ) : (
    <NotImplemented component={finalComponent} {...componentProps} />
  );
};

Component.displayName = 'Component';
Component.propTypes = {
  component: componentPropType.isRequired,
  as: PropTypes.elementType,
};
